import { BoxV2 as Box } from 'portal-commons';
import { FunctionComponent, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TableCell, TableRow } from '@material-ui/core';

import DescriptionTooltip from './DescriptionTooltip';
import { CnpCampaignListItem } from '../../types';
import { remapTCRStatus } from '../../utils';
import { convertTimeWithTimezone } from '@/utils/time';
import { SelectItem } from '@/utils/types';

interface DeclinedCnpCampaignListingRowProps {
  campaign: CnpCampaignListItem;
  rejectionCategoryOptions?: SelectItem[];
}

const DeclinedCnpCampaignListingRow: FunctionComponent<
  DeclinedCnpCampaignListingRowProps
> = ({ campaign, rejectionCategoryOptions = [] }) => {
  const history = useHistory();
  const location = useLocation();
  const rejectionCategoryMap = useMemo(
    () =>
      Object.fromEntries(
        rejectionCategoryOptions.map((option) => [option.value, option.label])
      ),
    [rejectionCategoryOptions]
  );
  const rejectionCategoryText = (campaign.sharedOnRejectionCategoryList ?? [])
    .map(
      (rejectionCategoryId) =>
        rejectionCategoryMap[rejectionCategoryId] ?? rejectionCategoryId
    )
    .join(', ');
  const description = `${
    campaign.sharedOnRejectionCategoryList ? `${rejectionCategoryText}: ` : ''
  }${campaign.sharedOnExplanation ?? ''}`;

  return (
    <TableRow
      style={{
        position: 'relative',
        height: '38px',
        cursor: 'pointer',
      }}
      onClick={() =>
        history.push({
          pathname: `/cnp-campaigns/${campaign.uid}`,
          state: {
            goBackPage: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        })
      }
    >
      <TableCell align="left" style={{ width: 'fit-content' }}>
        {campaign.uid}
      </TableCell>
      <TableCell align="left" style={{ width: 'fit-content' }}>
        {campaign.brandUid}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 'fit-content',
          maxWidth: '102px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span title={campaign.usecase}>{campaign.usecase}</span>
      </TableCell>
      <TableCell align="left" style={{ width: 'fit-content' }}>
        {campaign.registeredOnDate
          ? convertTimeWithTimezone(
              campaign.registeredOnDate,
              undefined,
              'MM/DD/YYYY'
            )
          : ''}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 'fit-content',
          maxWidth: '150px',
          wordBreak: 'keep-all',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span title={campaign.downstreamCnpName}>
          {campaign.downstreamCnpName}
        </span>
      </TableCell>
      <TableCell align="left" style={{ width: 'fit-content' }}>
        {campaign.sharedOnDate
          ? convertTimeWithTimezone(
              campaign.sharedOnDate,
              undefined,
              'MM/DD/YYYY'
            )
          : ''}
      </TableCell>
      <TableCell align="left" style={{ width: 'fit-content' }}>
        {campaign.sharedOnUpdateDate
          ? convertTimeWithTimezone(
              campaign.sharedOnUpdateDate,
              undefined,
              'MM/DD/YYYY'
            )
          : ''}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 'fit-content',
          maxWidth: '280px',
        }}
      >
        <DescriptionTooltip
          rejectionCategory={rejectionCategoryText}
          explanation={campaign.sharedOnExplanation ?? ''}
        >
          <Box
            sx={{
              wordBreak: 'keep-all',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description || 'N/A'}
          </Box>
        </DescriptionTooltip>
      </TableCell>
      <TableCell align="left" style={{ width: 'fit-content' }}>
        <span title={remapTCRStatus(campaign.status)}>
          {remapTCRStatus(campaign.status)}
        </span>
      </TableCell>
    </TableRow>
  );
};

export default DeclinedCnpCampaignListingRow;
